import { PlusOutlined } from '@ant-design/icons'
import { Button, ReorderableList } from '@lmc/ui-kit'
import { useCallback, useEffect, useState } from 'react'
import ActionPlanItem, {
  ActionPlanItemStatusOption,
  ActionPlanItemValue,
  ActionPlanStatus,
} from './ActionPlanItem'
import { v4 } from 'uuid'
import styled from 'styled-components'
import { ButtonVariant } from '@lmc/ui-kit/src/general/Button/Button'

export const valueToInnerValue = <T extends ActionPlanStatus>(
  defaultValue: T,
  item?: ActionPlanValue<T> | undefined
): ActionPlanInnerValue<T> => {
  const id = item?.id || v4()
  const title = item?.title || ''
  const status = item?.status || defaultValue
  const canEdit = item?.canEdit
  const canDelete = item?.canDelete

  const newItem: ActionPlanInnerValue<T> = {
    id,
    title,
    status,
    itemStoredID: item?.id,
    canDelete,
    canEdit,
  }

  return newItem
}

export const innerValueToValue = <T extends ActionPlanStatus>(
  defaultValue: T,
  item?: ActionPlanInnerValue<T> | undefined
): ActionPlanValue<T> => {
  const title = item?.title || ''
  const status = item?.status || defaultValue
  const canDelete = item?.canDelete
  const canEdit = item?.canEdit

  const newItem: ActionPlanValue<T> = {
    id: item?.itemStoredID as string | undefined,
    title,
    status,
    canDelete,
    canEdit,
  }

  return newItem
}

const ActionsInput = <T extends ActionPlanStatus>(
  props: ActionPlanInputProps<T>
) => {
  const {
    onChange,
    value = [],
    disabled,
    defaultStatusValue,
    options,
    variant = 'primary',
    buttonText,
  } = props

  const [items, setItems] = useState<ActionPlanInnerValue<T>[]>(
    value.map((item) => valueToInnerValue(defaultStatusValue, item))
  )

  useEffect(() => {
    const newItems = value.map((item) =>
      valueToInnerValue(defaultStatusValue, item)
    )
    if (!areItemsEqual(newItems, items)) {
      setItems(newItems)
    }
  }, [value])

  const addActionHandler = useCallback(() => {
    const newItem: ActionPlanInnerValue<T> =
      valueToInnerValue(defaultStatusValue)

    const newItems = [...items]
    newItems.push(newItem)

    changeHandler(newItems)
  }, [items])

  const changeHandler = useCallback(
    (newValue: ActionPlanInnerValue<T>[]) => {
      if (newValue !== items) {
        onChange?.(
          newValue.map((item) => innerValueToValue(defaultStatusValue, item))
        )

        setItems(newValue)
      }
    },
    [onChange]
  )

  const handleRemove = (id: string | number) => {
    const newItems = items.filter((item) => item.id !== id)

    changeHandler(newItems)
  }

  return (
    <Wrapper>
      <ReorderableList onChange={changeHandler} items={items}>
        <ListWrapper>
          {items.map((item) => (
            <ActionPlanItem<T>
              disabled={disabled}
              key={item.id}
              {...item}
              onRemove={handleRemove}
              onChange={(value) =>
                itemChangeHandler(value, items, changeHandler)
              }
              options={options}
              defaultValue={defaultStatusValue}
            />
          ))}
        </ListWrapper>
      </ReorderableList>
      {disabled || (
        <StyledButton
          style={{ marginTop: 10 }}
          icon={<PlusOutlined />}
          variant={variant}
          onClick={addActionHandler}
          type="button"
          milestone={variant === 'text'}
        >
          {buttonText}
        </StyledButton>
      )}
    </Wrapper>
  )
}

interface StyledButtonProps {
  milestone?: boolean
}

const StyledButton = styled(Button)<StyledButtonProps>`
  ${({ milestone, theme }) =>
    milestone
      ? `
        position: absolute;
        top: -65px;
        right: 0;
		${theme.mediaQueries.mobileDown} {
   		 position: static;
 		 }
      `
      : `
        position: static;
      `}
`

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Wrapper = styled.div`
  width: 100%;
`

export const itemChangeHandler = <T extends ActionPlanStatus>(
  value: ActionPlanInnerValue<T>,
  items: ActionPlanInnerValue<T>[],
  changeHandler: (value: ActionPlanInnerValue<T>[]) => void
) => {
  const newItems = [...items]

  const oldItem = newItems.find((item) => item.id === value.id)

  if (!oldItem) {
    return
  }

  const oldItemIndex = newItems.indexOf(oldItem)

  newItems[oldItemIndex] = {
    ...value,
  }

  changeHandler(newItems)
}

export interface ActionPlanValue<T extends ActionPlanStatus>
  extends Omit<ActionPlanItemValue<T>, 'itemStoredID' | 'id'> {
  id: string | undefined
}
type ActionPlanInnerValue<T extends ActionPlanStatus> = ActionPlanItemValue<T>

type ActionPlanInputProps<T extends ActionPlanStatus> = {
  onChange?: (value: ActionPlanValue<T>[]) => void
  value?: ActionPlanValue<T>[]
  disabled?: boolean
  defaultStatusValue: T
  options: ActionPlanItemStatusOption<T>[]
  variant?: ButtonVariant
  buttonText: string
}

const areItemsEqual = <T extends ActionPlanStatus>(
  arr1: ActionPlanInnerValue<T>[],
  arr2: ActionPlanInnerValue<T>[]
) => {
  if (arr1.length !== arr2.length) return false

  return arr1.every((item1, index) => {
    const item2 = arr2[index]
    return (
      item1.title === item2.title &&
      item1.status === item2.status &&
      item1.canEdit === item2.canEdit &&
      item1.canDelete === item2.canDelete
    )
  })
}

export default ActionsInput
